<template>
    <div class="header-filters-wrapper">
        <div v-if="props.filterHeader.allowFilter" class="flex-column-with-gap">
            <h4 v-if="!props.hideFilterLabel">Filter</h4>
            <template v-if="type === 'number'">
                <min-max-selector :model-value="numericRange" @update:model-value="updateNumericRange" />
                <div class="space-between">
                    <div class="checkbox-wrapper">
                        <label for="absoluteValues">Use absolute values</label>
                        <select-button
                            :model-value="useAbsoluteValues"
                            :allow-empty="false"
                            id="absoluteValues"
                            :options="YES_NO_OPTIONS"
                            option-label="title"
                            option-value="value"
                            @update:model-value="updateUseAbsoluteValues"
                        />
                        <info-icon-with-tooltip tooltip-text="Allows the above filter to be applied to both positive and negative values<br />(e.g. a range of 10 to 100 with absolute values on will allow values of 10-100 and -10 to -100)" direction="top" />
                    </div>
                </div>
            </template>
            <template v-else-if="type === 'text'">
                <div class="flex-row-with-gap">
                    <select-button
                        :model-value="excludeSelection"
                        :options="EXCLUDE_SELECTION_OPTIONS"
                        id="excludeSelection"
                        option-label="title"
                        option-value="value"
                        @update:model-value="updateExcludeSelection"
                    />
                    <info-icon-with-tooltip tooltip-text="When 'Exclude' is selected, the options below dictate what not to show." direction="top" />
                </div>
                <div class="flex-row-with-gap">
                    <div class="plant-dropdown" v-if="usePlantDropdown">
                        <select-button
                            :model-value="selectedPlantOption"
                            :options="PLANT_DROPDOWN_OPTIONS"
                            option-label="name"
                            option-value="id"
                            :allow-empty="false"
                            @update:model-value="updatePlantSelection"
                        >
                            <template #option="{ option }">
                                <span>{{ option.name }}</span>
                                <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                            </template>
                        </select-button>
                        <plant-selector
                            v-if="selectedPlantOption === 'plant'"
                            :country-id="userSettingsStore.userCountry"
                            multi-select
                            full-width
                            :selected-plant-ids="selectedValuesForFilter"
                            @update:model-value-multiple="updateSelectValues($event.map(x => x.id))"
                        />
                        <primevue-select
                            v-else
                            :model-value="selectedPlantGroup"
                            option-label="name"
                            option-value="id"
                            :options="plantGroupOptions"
                            placeholder="Select group"
                            @update:model-value="updatePlantGroup"
                        />
                    </div>
                    <fuel-selector
                        v-else-if="useFuelDropdown"
                        multi-select
                        full-width
                        :options="fuelOptionsForFilter"
                        :model-value="selectedValuesForFilter"
                        @update:model-value-multiple="updateSelectValues($event.map(x => x.name))"
                    />
                    <spark-multi-select
                        v-else
                        :model-value="selectedValuesForFilter"
                        placeholder="Select value to filter by"
                        :options="textOptions"
                        :virtual-scroller-options="VIRTUAL_SCROLLER_OPTIONS"
                        @update:model-value="updateSelectValues"
                    />
                    <primevue-button v-tooltip="'Clear values'" text @click="updateSelectValues([])">
                        <font-awesome-icon :icon="['fas', 'rotate-left']" />
                    </primevue-button>
                </div>
            </template>
            <template v-else-if="type === 'date'">
                <advanced-date-picker
                    clearable
                    :model-value="selectedDate"
                    @submit="updateSelectedDate"
                    @reset:filters="resetDateFilters"
                />
            </template>
        </div>
        <div v-if="props.filterHeader.allowSort" class="flex-column-with-gap">
            <h4>Sort</h4>
            <slot
                name="sort"
                :filter-header="props.filterHeader"
                :header-to-sort="props.headerToSort"
                :local-sort-direction="localSortDirection"
                :update-sort-direction="updateSortDirection"
            >
                <select-button
                    :model-value="localSortDirection"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    :options="SORT_DIRECTION_OPTIONS"
                    @update:model-value="updateSortDirection"
                />
            </slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { IFlagOption } from '@/interfaces/general/IFlagOption.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IMinMax } from '@/interfaces/general/IMinMax.ts';
import { TTableColumnType } from '@/types/table/TTableColumnType.ts';
import PrimevueSelect from 'primevue/select';
import PrimevueButton from 'primevue/button';
import { VirtualScrollerProps } from 'primevue/virtualscroller';
import SparkMultiSelect from '@/components/common/SparkMultiSelect.vue';
import SelectButton from 'primevue/selectbutton';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { IFuelType } from '@/interfaces/powerMarkets/IFuelType.ts';
import { IGenericTableRowFormatting } from '@/interfaces/powerMarkets/table/IGenericTableRowFormatting.ts';
import { IGenericTableHeaderRow } from '@/interfaces/powerMarkets/table/IGenericTableHeaderRow.ts';
import {
    IDateHeadingFilters, IHeadingFilters, INumericHeadingFilters, ITextHeadingFilters,
} from '@/interfaces/powerMarkets/table/IHeadingFilters.ts';
import FuelSelector from '@/components/powerMarkets/selectors/FuelSelector.vue';
import PlantSelector from '@/components/powerMarkets/selectors/PlantSelector.vue';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import MinMaxSelector from '@/components/powerMarkets/input/MinMax.vue';
import { ITableHeaderToSort } from '@/interfaces/powerMarkets/table/ITableFilters.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import AdvancedDatePicker from '../date-picker/AdvancedDatePicker.vue';

dayjs.extend(minMax);

const userSettingsStore = useUserSettingsStore();
const dataStore = useDataStore();

const PLANT_DROPDOWN_OPTIONS: Array<IIdName> = [
    { id: 'plant', name: 'Select plants' },
    { id: 'group', name: 'My groups' },
];

const EXCLUDE_SELECTION_OPTIONS: Array<IFlagOption> = [
    { title: 'Include selection', value: false },
    { title: 'Exclude selection', value: true },
];

const SORT_DIRECTION_OPTIONS: Array<IIdName> = [
    { name: 'Ascending', id: 'ascending' },
    { name: 'Descending', id: 'descending' },
];

const YES_NO_OPTIONS: Array<IFlagOption> = [
    { title: 'Yes', value: true },
    { title: 'No', value: false },
];

const VIRTUAL_SCROLLER_OPTIONS: VirtualScrollerProps = {
    itemSize: 38,
};

const props = defineProps<{
    currentFilters: IHeadingFilters | null;
    headerToSort: ITableHeaderToSort;
    filterProperty: IGenericTableRowFormatting;
    filterHeader: IGenericTableHeaderRow;
    hideFilterLabel?: boolean;
}>();

const emit = defineEmits<{
    'update:sort-direction-ascending': [boolean];
    'update:filters': [IHeadingFilters];
}>();

const textOptions = ref<Array<string | number>>([]);
const excludeSelection = ref<boolean>(false);
const localSortDirection = ref<string | null>(null);
const selectedValuesForFilter = ref<Array<string>>([]);
const selectedDate = ref<IAdvancedDatePickerModel | null>(null);
const numericRange = ref<IMinMax>({ min: null, max: null });
const useAbsoluteValues = ref<boolean>(false);
const selectedPlantGroup = ref<string | null>(null);
const plantGroupOptions = ref<Array<IIdName>>([
    { name: 'Show all', id: 'all' },
    { name: 'Show units on bingo board', id: 'bingoEnact' },
    { name: 'Show units not on bingo board', id: 'notBingoEnact' },
]);
const selectedPlantOption = ref<string>('plant');

const type = ref<TTableColumnType>('text');
const usePlantDropdown = ref<boolean>(false);
const useFuelDropdown = ref<boolean>(false);

const fuelOptionsForFilter = computed<Array<IFuelType> | undefined>(() => {
    if (!useFuelDropdown.value) return undefined;
    const uniqueValues = textOptions.value;
    if (!uniqueValues.length) return undefined;
    const mappedFuels = dataStore.staticStore.fuelTypes.filter((fuelType: IFuelType) => uniqueValues.includes(fuelType.name) || uniqueValues.includes(fuelType.id));
    if (!mappedFuels.length) return undefined;
    return mappedFuels;
});

function isFuelHeader (header: IGenericTableHeaderRow): boolean {
    return header.title === 'Fuel' || header.title === 'Secondary Fuel';
}

function resetDateFilters (): void {
    selectedDate.value = null;
    updateDateFilter();
}

function updateNumericRange (newSetting: IMinMax): void {
    numericRange.value = newSetting;
    updateNumericFilter();
}

function updateUseAbsoluteValues (newSetting: boolean): void {
    useAbsoluteValues.value = newSetting;
    updateNumericFilter();
}

function updateExcludeSelection (newSetting: boolean): void {
    excludeSelection.value = newSetting;
    updateTextFilter();
}

function updateSelectValues (newSetting: Array<string>): void {
    if (usePlantDropdown.value) selectedPlantGroup.value = null;
    selectedValuesForFilter.value = newSetting;
    updateTextFilter();
}

function updatePlantGroup (newSetting: string): void {
    selectedValuesForFilter.value = [];
    selectedPlantGroup.value = newSetting;
    updateTextFilter();
}

function updateSelectedDate (updatedDate: IAdvancedDatePickerModel | null): void {
    selectedDate.value = updatedDate;
    updateDateFilter();
}

function updatePlantSelection (newSetting: string) :void {
    selectedPlantOption.value = newSetting;
}

function updateNumericFilter (): void {
    const numericFilter: INumericHeadingFilters = {
        type: 'numeric',
        options: {
            min: numericRange.value.min,
            max: numericRange.value.max,
            absolute: useAbsoluteValues.value,
        },
        noFilter: !numericRange.value.min && !numericRange.value.max,
    };
    emit('update:filters', numericFilter);
}

function updateTextFilter (): void {
    const textFilter: ITextHeadingFilters = {
        type: 'text',
        options: {
            values: selectedValuesForFilter.value,
            group: selectedPlantGroup.value,
            exclude: excludeSelection.value,
        },
        noFilter: !selectedValuesForFilter.value.length && (!selectedPlantGroup.value || selectedPlantGroup.value === 'all'),
    };

    emit('update:filters', textFilter);
}

function updateDateFilter (): void {
    const dateFilter: IDateHeadingFilters = {
        type: 'date',
        options: {
            date: selectedDate.value,
        },
        noFilter: !selectedDate.value,
    };

    emit('update:filters', dateFilter);
}

function updateSortDirection (newSetting: string): void {
    localSortDirection.value = newSetting;
    if (!localSortDirection.value) return;
    emit('update:sort-direction-ascending', localSortDirection.value === 'ascending');
}

function setZoneOptions (uniqueValues: Array<string | number>): void {
    if (!uniqueValues.length) return;
    if (!uniqueValues.every(x => typeof x === 'string')) return;
    textOptions.value = uniqueValues.toSorted((a, b) => {
        const regex = /^Z(\d+)$/;
        const aMatch = a.match(regex);
        const bMatch = b.match(regex);
        if (aMatch && bMatch) {
            return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
        }
        return 0;
    });
}

function initialise (): void {
    type.value = props.filterProperty.cellType;

    if (props.headerToSort.id === props.filterHeader.id) {
        localSortDirection.value = props.headerToSort.ascending ? 'ascending' : 'descending';
    }

    const uniqueValues = props.filterHeader.uniqueValues?.filter(x => !!x) ?? [];
    switch (props.filterProperty.cellType) {
        case 'number':
            if (props.currentFilters && props.currentFilters.type === 'numeric') {
                numericRange.value.min = props.currentFilters.options.min;
                numericRange.value.max = props.currentFilters.options.max;
                useAbsoluteValues.value = props.currentFilters.options.absolute ?? false;
            }
            return;
        case 'text':
            if (props.currentFilters && props.currentFilters.type === 'text') {
                excludeSelection.value = props.currentFilters?.options.exclude ?? false;
                selectedValuesForFilter.value = props.currentFilters.options.values.filter(x => x !== null);
            }

            if (props.filterHeader.title.toLowerCase().includes('zone')) {
                setZoneOptions(uniqueValues);
            } else {
                textOptions.value = uniqueValues ?? [];
            }

            if (props.filterHeader.isPlant) {
                usePlantDropdown.value = true;
                const plantsByGroup = userSettingsStore.userSettings.otherSettings.data.chosenPlantsByGroup;
                Object.keys(plantsByGroup).forEach(group => {
                    plantGroupOptions.value.push({
                        name: group,
                        id: group,
                    });
                });
                if (props.currentFilters && props.currentFilters.type === 'text') {
                    const currentGroup = props.currentFilters.options.group;
                    if (currentGroup) {
                        selectedPlantGroup.value = plantGroupOptions.value.find(p => p.id === currentGroup)?.id ?? null;
                        selectedValuesForFilter.value = [];
                        selectedPlantOption.value = 'group';
                    }
                }
                return;
            }
            if (isFuelHeader(props.filterHeader)) {
                useFuelDropdown.value = true;
            }
            return;
        case 'date':
            if (props.currentFilters && props.currentFilters.type === 'date') {
                selectedDate.value = props.currentFilters.options.date;
            }
    }
}

initialise();

watch(
    () => props.currentFilters,
    (filters) => {
        if (props.filterProperty.cellType !== 'date') return;
        if (filters && filters.type === 'date') {
            selectedDate.value = filters.options.date;
        } else {
            selectedDate.value = null;
        }
    },
    { deep: true },
);
</script>

<style lang="scss" scoped>

.header-filters-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    padding: var(--defaultSpacing);
    background-color: var(--neutral0);
    border-radius: var(--borderRadiusOuter);

    :deep(.p-multiselect-label-container) {
        max-width: 300px;
    }
}

.space-between {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.checkbox-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--defaultSpacing);

    label {
        width: fit-content;
    }
}

.plant-dropdown {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
}

.p-tooltip-text {
    max-width: 300px!important;
}
</style>
